/**
 * +world-resources: ground pickups. Every pile on the ground is one instance of a merged gem
 * mesh (coin, ingot, scale, vial, hide roll, eye crystal; the instance picks its shape in the
 * vertex shader), so all loot is one draw call. Motion is derived from sim state each frame:
 *   arc     from -> pos between spawnedAt and landAt (ballistic hop, fast spin)
 *   land    two quick bounces with squash, then a slow hover bob and spin
 *   magnet  once the sim sets magnetSince, it zips into the looter (accelerating, shrinking)
 *   pop     the sim's `pickup` event: a rarity-coloured flash where the looter stands
 * The glow / beam / trail effects are emitted by the caller through `PickupFxSink`.
 */
import * as THREE from 'three';
import type { DeepReadonly, PickupRarity, PickupState, WorldResourceKind } from '@keystone/sim';
import { groundY } from '../../ground';
import { ownedToonMaterial } from '../../style';
import { RARITY_BEAM, RARITY_GLOW, RARITY_GLOW_K, RESOURCE_SHAPE, RESOURCE_TINT } from './palette';

/** Rest hover height of a landed pickup's centre above the floor (yd). */
export const HOVER_Y = 0.42;
/** Peak height of the burst arc above the straight line (yd). */
export const ARC_PEAK = 1.5;

/** Height of the burst arc over its 0..1 progress, relative to the floor (start ≈ 0.7, end ≈ 0.18). */
export function arcHeight(u: number, peak = ARC_PEAK): number {
  const k = Math.min(1, Math.max(0, u));
  return 0.7 + (0.18 - 0.7) * k + 4 * peak * k * (1 - k);
}

/** Height after landing (s since landAt): decaying bounces that blend into the hover bob. */
export function landedHeight(tb: number, timeS: number, phase: number): number {
  if (tb < 0) return 0.18;
  const bounce = 0.38 * Math.exp(-tb * 6.5) * Math.abs(Math.sin(tb * 15));
  const k = Math.min(1, Math.max(0, (tb - 0.25) / 0.45));
  const s = k * k * (3 - 2 * k);
  const hover = HOVER_Y + Math.sin(timeS * 2.6 + phase) * 0.06;
  return 0.18 + bounce + s * (hover - 0.18);
}

export interface PickupFxSink {
  /** Called once per visible pickup per frame (glow, beam, ground disc). `air` = still in flight. */
  glow(x: number, y: number, z: number, groundY: number, rarity: PickupRarity, k: number, beam: number, air: boolean, phase: number): void;
  /** Magnet trail sample (world pos + velocity). */
  trail(x: number, y: number, z: number, vx: number, vy: number, vz: number, rarity: PickupRarity): void;
  /** Collected: pop at this spot. */
  pop(x: number, y: number, z: number, rarity: PickupRarity, resource: WorldResourceKind): void;
  /** Landing thud (first floor contact). */
  land(x: number, y: number, z: number, rarity: PickupRarity): void;
}

interface Slot {
  id: string;
  used: boolean;
  stamp: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  magnetAt: number;
  landed: boolean;
  collected: boolean;
  phase: number;
  spin: number;
  rarity: PickupRarity;
  resource: WorldResourceKind;
  scale: number;
}

const blankSlot = (): Slot => ({ id: '', used: false, stamp: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, magnetAt: -1, landed: false, collected: false, phase: 0, spin: 0, rarity: 'common', resource: 'gold', scale: 1 });

export class PickupsView {
  readonly mesh: THREE.InstancedMesh;
  readonly capacity: number;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.MeshToonMaterial;
  private readonly iShape: THREE.InstancedBufferAttribute;
  private readonly iGlow: THREE.InstancedBufferAttribute;
  private readonly slots: Slot[];
  private readonly byId = new Map<string, Slot>();
  private stamp = 0;
  private readonly m4 = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly v = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly c = new THREE.Color();

  constructor(capacity = 128) {
    this.capacity = capacity;
    this.geo = buildGemGeometry();
    this.iShape = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.iGlow = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.iShape.setUsage(THREE.DynamicDrawUsage);
    this.iGlow.setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('iShape', this.iShape);
    this.geo.setAttribute('iGlow', this.iGlow);
    this.mat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
    this.mat.name = 'lootPickups';
    const toon = this.mat.onBeforeCompile.bind(this.mat);
    this.mat.onBeforeCompile = (sh, r) => {
      toon(sh, r);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float aShape; attribute float iShape; attribute vec3 iGlow; varying vec3 vLootGlow;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nif (abs(aShape - iShape) > 0.5) transformed = vec3(0.0);\nvLootGlow = iGlow;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vLootGlow;')
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vLootGlow;');
    };
    this.mat.customProgramCacheKey = () => 'lootPickups';
    this.mesh = new THREE.InstancedMesh(this.geo, this.mat, capacity);
    this.mesh.name = 'lootPickups';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, this.c.setRGB(1, 1, 1));
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.userData['noStyle'] = true;
    this.slots = Array.from({ length: capacity }, blankSlot);
  }

  /** Live slots (tests / stats). */
  get active(): number {
    return this.byId.size;
  }

  private alloc(p: DeepReadonly<PickupState>): Slot | null {
    for (const s of this.slots) {
      if (s.used) continue;
      s.used = true;
      s.id = p.id;
      s.magnetAt = -1;
      s.landed = false;
      s.collected = false;
      s.vx = s.vy = s.vz = 0;
      // Stable per-pile phase from the id (bob / spin offsets).
      let h = 0;
      for (let i = 0; i < p.id.length; i++) h = (h * 31 + p.id.charCodeAt(i)) | 0;
      s.phase = ((h >>> 0) % 628) / 100;
      s.spin = s.phase;
      s.rarity = p.rarity;
      s.resource = p.resource;
      s.scale = 1;
      this.byId.set(p.id, s);
      return s;
    }
    return null;
  }

  /** Mark a pile collected (from the sim's `pickup` event) so it pops instead of vanishing. */
  collect(pickupId: string): void {
    const s = this.byId.get(pickupId);
    if (s) s.collected = true;
  }

  /**
   * Advance and draw. `looter` is the looter's interpolated position (null: none / hidden).
   * `timeMs` is the frame's interpolated sim time; `dt` real seconds.
   */
  update(pickups: readonly DeepReadonly<PickupState>[], timeMs: number, dt: number, looter: { x: number; y: number; h?: number } | null, sink: PickupFxSink): void {
    const stamp = ++this.stamp;
    const tS = timeMs / 1000;
    let n = 0;
    for (const p of pickups) {
      const s = this.byId.get(p.id) ?? this.alloc(p);
      if (!s) continue;
      s.stamp = stamp;
      const gy = p.z ?? groundY(p.pos.x, p.pos.y); // +glide: a sky chest's piles sit on its platform
      let air = false;
      if (p.magnetSince !== null && looter) {
        // Zip into the looter: accelerate along the line to their chest, with a little lift.
        if (s.magnetAt < 0) {
          s.magnetAt = timeMs;
          if (!s.landed) {
            s.x = p.pos.x;
            s.z = -p.pos.y;
            s.y = gy + HOVER_Y;
          }
        }
        const ty = groundY(looter.x, looter.y) + (looter.h ?? 0) + 1.0;
        const dx = looter.x - s.x;
        const dz = -looter.y - s.z;
        const dy = ty - s.y;
        const d = Math.hypot(dx, dy, dz);
        const tm = (timeMs - s.magnetAt) / 1000;
        const speed = 7 + 55 * tm;
        const step = Math.min(d, speed * dt);
        const k = d > 1e-4 ? step / d : 0;
        // A small upward hop as it lifts off, fading over the first ~0.3 s.
        const lift = 2.2 * Math.exp(-tm * 7) * dt;
        const idt = 1 / Math.max(1e-3, dt);
        s.vx = dx * k * idt;
        s.vy = (dy * k + lift) * idt;
        s.vz = dz * k * idt;
        s.x += dx * k;
        s.y += dy * k + lift;
        s.z += dz * k;
        s.scale = Math.max(0.4, Math.min(1, d / 1.4));
        s.spin += dt * 14;
        sink.trail(s.x, s.y, s.z, s.vx, s.vy, s.vz, s.rarity);
      } else if (timeMs < p.landAt) {
        const u = (timeMs - p.spawnedAt) / Math.max(1, p.landAt - p.spawnedAt);
        const k = Math.min(1, Math.max(0, u));
        s.x = p.from.x + (p.pos.x - p.from.x) * k;
        s.z = -(p.from.y + (p.pos.y - p.from.y) * k);
        const fy = p.z ?? groundY(p.from.x, p.from.y);
        s.y = fy + (gy - fy) * k + arcHeight(k);
        s.spin += dt * 11;
        s.scale = 0.6 + 0.4 * Math.min(1, k * 3);
        air = true;
      } else {
        if (!s.landed) {
          s.landed = true;
          sink.land(p.pos.x, gy, -p.pos.y, s.rarity);
        }
        const tb = (timeMs - p.landAt) / 1000;
        s.x = p.pos.x;
        s.z = -p.pos.y;
        s.y = gy + landedHeight(tb, tS, s.phase);
        s.spin += dt * (1.6 + 6 * Math.exp(-tb * 3));
        s.scale = 1;
      }
      if (n >= this.capacity) continue;
      // Squash on each bounce contact (cheap: driven by height above the rest line).
      const sq = air ? 1 : 1 - 0.25 * Math.min(1, Math.max(0, 0.32 - (s.y - gy)) / 0.32);
      const shape = RESOURCE_SHAPE[s.resource];
      const tilt = shape === 0 ? 0 : 0.35;
      this.q.setFromEuler(this.e.set(tilt * Math.sin(s.spin * 0.5 + s.phase), s.spin, 0));
      const sc = 1.25 * s.scale;
      this.m4.compose(this.v.set(s.x, s.y, s.z), this.q, this.s.set(sc / Math.sqrt(sq), sc * sq, sc / Math.sqrt(sq)));
      this.mesh.setMatrixAt(n, this.m4);
      const tint = RESOURCE_TINT[s.resource];
      this.mesh.setColorAt(n, this.c.setRGB(tint[0], tint[1], tint[2]));
      const g = RARITY_GLOW[s.rarity];
      const gk = 0.16 + 0.1 * RARITY_GLOW_K[s.rarity] + 0.05 * Math.sin(tS * 3 + s.phase);
      this.iShape.setX(n, shape);
      this.iGlow.setXYZ(n, g[0] * gk, g[1] * gk, g[2] * gk);
      n++;
      sink.glow(s.x, s.y, s.z, gy, s.rarity, s.magnetAt >= 0 ? s.scale : 1, air || s.magnetAt >= 0 ? 0 : RARITY_BEAM[s.rarity], air, s.phase);
    }
    // Piles that left the sim: pop if collected, else just drop them.
    for (const s of this.slots) {
      if (!s.used || s.stamp === stamp) continue;
      if (s.collected) {
        if (looter) sink.pop(looter.x, groundY(looter.x, looter.y) + (looter.h ?? 0) + 1.0, -looter.y, s.rarity, s.resource);
        else sink.pop(s.x, s.y, s.z, s.rarity, s.resource);
      }
      s.used = false;
      this.byId.delete(s.id);
    }
    this.mesh.count = n;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.instanceColor!.needsUpdate = true;
      this.iShape.needsUpdate = true;
      this.iGlow.needsUpdate = true;
    }
  }

  clear(): void {
    for (const s of this.slots) s.used = false;
    this.byId.clear();
    this.mesh.count = 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.dispose();
    this.geo.dispose();
    this.mat.dispose();
  }
}

// ---- gem geometry ------------------------------------------------------------------------

const tc = new THREE.Color();
function shaped(geo: THREE.BufferGeometry, hex: number, shape: number, ao: [number, number] = [0.75, 1.12]): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  g.deleteAttribute('uv');
  if (!g.attributes['normal']) g.computeVertexNormals();
  const pos = g.attributes['position'] as THREE.BufferAttribute;
  g.computeBoundingBox();
  const y0 = g.boundingBox!.min.y;
  const y1 = g.boundingBox!.max.y;
  tc.setHex(hex);
  const col = new Float32Array(pos.count * 3);
  const sh = new Float32Array(pos.count).fill(shape);
  for (let i = 0; i < pos.count; i++) {
    const t = y1 > y0 ? (pos.getY(i) - y0) / (y1 - y0) : 1;
    const k = ao[0] + (ao[1] - ao[0]) * t;
    col[i * 3] = tc.r * k;
    col[i * 3 + 1] = tc.g * k;
    col[i * 3 + 2] = tc.b * k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aShape', new THREE.BufferAttribute(sh, 1));
  return g;
}

/** All six pickup shapes in one geometry (vertex attribute aShape = RESOURCE_SHAPE index). */
export function buildGemGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // 0 gold: a fat upright coin with a raised rim.
  parts.push(shaped(new THREE.CylinderGeometry(0.17, 0.17, 0.055, 16).rotateX(Math.PI / 2), 0xf2c14e, 0, [0.85, 1.1]));
  parts.push(shaped(new THREE.TorusGeometry(0.165, 0.022, 4, 16), 0xffd877, 0, [1, 1]));
  // 1 fangsteel: a bevelled ingot with a bright top.
  parts.push(shaped(new THREE.BoxGeometry(0.34, 0.1, 0.17), 0x8e9bb0, 1, [0.7, 1]));
  parts.push(shaped(new THREE.BoxGeometry(0.26, 0.05, 0.11).translate(0, 0.07, 0), 0xc6d0de, 1, [1, 1.1]));
  // 2 coil scale: a jade diamond plate standing up.
  parts.push(shaped(new THREE.OctahedronGeometry(0.2, 0).scale(1, 1.3, 0.35), 0x4cc89c, 2));
  // 3 viper venom: a round vial with a cork.
  parts.push(shaped(new THREE.SphereGeometry(0.13, 10, 8).translate(0, -0.04, 0), 0x72e06a, 3, [0.8, 1.2]));
  parts.push(shaped(new THREE.CylinderGeometry(0.045, 0.05, 0.12, 8).translate(0, 0.12, 0), 0xa8e0b0, 3, [1, 1]));
  parts.push(shaped(new THREE.CylinderGeometry(0.05, 0.05, 0.05, 8).translate(0, 0.2, 0), 0x6b4a2e, 3, [1, 1]));
  // 4 moltskin: a rolled hide with a cord.
  parts.push(shaped(new THREE.CylinderGeometry(0.09, 0.09, 0.34, 10).rotateZ(Math.PI / 2), 0xc8a070, 4, [0.75, 1.1]));
  parts.push(shaped(new THREE.TorusGeometry(0.095, 0.018, 4, 12).rotateY(Math.PI / 2), 0x6b4a2e, 4, [1, 1]));
  // 5 serpent's eye fragment: a tall amber crystal.
  parts.push(shaped(new THREE.OctahedronGeometry(0.16, 0).scale(0.85, 1.7, 0.85), 0xffa443, 5, [0.8, 1.3]));
  const out = new THREE.BufferGeometry();
  let total = 0;
  for (const p of parts) total += (p.attributes['position'] as THREE.BufferAttribute).count;
  for (const name of ['position', 'normal', 'color', 'aShape'] as const) {
    const size = name === 'aShape' ? 1 : 3;
    const arr = new Float32Array(total * size);
    let o = 0;
    for (const p of parts) {
      const a = p.attributes[name] as THREE.BufferAttribute;
      arr.set(a.array as Float32Array, o);
      o += a.count * size;
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, size));
  }
  parts.forEach((p) => p.dispose());
  out.computeBoundingSphere();
  return out;
}
